import { Pencil, GripVertical, BarChart3, CheckCircle2 } from "lucide-react";
import DOMPurify from "dompurify";
import Button from "../../ui/Button";
import "./_plan-card.scss";

/**
 * Shared plan/product pricing-display card - moved from components/PlanCard.
 * Real consumers: ProductFormBody/PlanFormBody (live create/edit previews),
 * DraggablePlanCard/PlansAndBundleView (bundle gallery), PlanBrowser
 * (self-serve checkout upsell/downsell), PricingPage (public pricing page).
 *
 * `handleDealModalToggle` (a second, always-no-op callback fired alongside
 * `handlePlanEditModal` on the pen-icon click) was dropped - confirmed via
 * grep across all 6 real consumers that none of them pass it.
 *
 * Icons swapped from raw SVG imports to lucide-react (rule 4) - `pen.svg`/
 * `drag_indicator.svg` stay as asset files (still used by `ManageBundlesModal`/
 * `CustomerContactModal`), only this component's own usage changed. `graph.svg`/
 * `circle-tick.svg` were exclusive to this component and were deleted outright.
 */
const PlanCard = ({
  data,
  title,
  handlePlanEditModal,
  isPenIcon,
  isDrag,
  isPreview,
  isAddOn = true,
  isChoosePlan = false,
  isPricing = false,
  planStatus,
  handleChoosePlan,
  checkoutMode,
  previousPlan,
  // Admin authoring previews (PlanFormBody/ProductFormBody) render before a
  // market is chosen, so a placeholder "$" is a reasonable stand-in there.
  // Customer-facing checkouts (PlanBrowser) pass "" instead - showing a
  // hardcoded "$" for a plan with no resolved market misrepresents the real
  // currency rather than honestly showing nothing, same as OrderSummary's
  // own fallback for the identical missing-market case.
  currencyFallback = "$",
}) => {
  const {
    planName,
    productName,
    description,
    pricingModel,
    market,
    price,
    amount,
    pricingTiers,
    pricingCycle,
    features,
    addOns,
    usageCompilationsArray,
    unitName,
    billingCycle,
  } = data || {};

  // A returning self-serve customer with no subscription yet has no previous plan to compare
  // against, so upgrade/downgrade wording would be wrong (it read "Degrade Plan").
  const isUpsellMode = checkoutMode === "upsell" && Boolean(previousPlan?._id);

  const lowercaseFirstLetterOfWords = (text) => {
    return (
      text
        ?.split(" ")
        ?.map((word) => word.charAt(0).toLowerCase() + word.slice(1))
        ?.join(" ") || ""
    );
  };
  return (
    <div className="plan-card">
      <div className={`plan-card__header ${isDrag ? "plan-card__header--drag" : ""}`}>
        <div className={`plan-card__head-content ${isDrag ? "plan-card__head-content--drag" : ""}`}>
          {isDrag && <GripVertical className="plan-card__drag-icon" size={16} />}
          <h3 className="plan-card__title">
            {planName || productName
              ? title === "Product"
                ? productName
                : planName
              : `${title === "Product" ? title : "Plan"} title`}
          </h3>

          {isPenIcon && (
            <div onClick={() => handlePlanEditModal?.(data)} className="plan-card__edit-icon">
              <Pencil size={10} />
            </div>
          )}
        </div>
        <div className="plan-card__desc">
          <div
            // `description` is free text, not authored HTML - unsanitized,
            // this executed arbitrary JS via e.g. `<img src=x onerror=...>`
            // for anyone viewing this card. This component is also used by
            // PlanBrowser (self-serve checkout) and PricingPage (public
            // pricing page), so an unsanitized description here was
            // exploitable outside the admin app too, not just internally
            // like Products' equivalent bug. Confirmed live during the
            // Plans CRUD test.
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                description !== "<p><br></p>" && description !== ""
                  ? description
                  : "Description"
              ),
            }}
          />
        </div>

        {isChoosePlan && (
          <div className="plan-card__choose-plan-container">
            {!isPricing ? (
              <div
                onClick={() => handleChoosePlan?.(data)}
                className={`plan-card__choose-plan-btn ${
                  isUpsellMode
                    ? previousPlan?._id === data?._id
                      ? planStatus === "active"
                        ? "plan-card__choose-plan-btn--current"
                        : "plan-card__choose-plan-btn--choose"
                      : previousPlan?.amount < data?.amount
                      ? "plan-card__choose-plan-btn--upsell"
                      : "plan-card__choose-plan-btn--downsell"
                    : planStatus === "active"
                    ? "plan-card__choose-plan-btn--current"
                    : "plan-card__choose-plan-btn--choose"
                }`}
              >
                {planStatus !== "active" && <BarChart3 size={16} />}
                <span className="plan-card__choose-plan-text">
                  {isUpsellMode
                    ? previousPlan?._id === data?._id
                      ? "Current Plan"
                      : previousPlan?.amount < data?.amount
                      ? "Upgrade Plan"
                      : "Degrade Plan"
                    : planStatus === "active"
                    ? /* D40 - outside upsell mode "active" only means
                         "selected in this wizard right now", not "already
                         an active subscription" - a brand-new visitor's
                         freshly-picked plan must not read "Current Plan". */
                      "Selected"
                    : "Choose Plan"}
                </span>
              </div>
            ) : (
              <Button
                type="submit"
                data-simplify-plan-id={data?._id}
                className={`plan-card__choose-plan-btn ${
                  isUpsellMode
                    ? previousPlan?._id === data?._id
                      ? "plan-card__choose-plan-btn--upsell"
                      : "plan-card__choose-plan-btn--downsell"
                    : planStatus === "active"
                    ? "plan-card__choose-plan-btn--current"
                    : "plan-card__choose-plan-btn--choose"
                }`}
                variant="contained"
                size="sm"
                icon={planStatus !== "active" && <BarChart3 size={16} />}
              >
                {planStatus === "active" ? "Current plan" : "Choose plan"}
              </Button>
            )}
          </div>
        )}

        <div className="plan-card__price">
          {["Flat Fee", "Percent"].includes(pricingModel) && (
            <p className="plan-card__value">
              <span className="plan-card__currency">
                {`${market?.currency ? market?.currency?.[0]?.sign : currencyFallback}`}
              </span>
              {title === "Product" && price && (
                <span className="plan-card__amount">{price ? price : "Enter amount"}</span>
              )}
              {title === "Plan" && amount && (
                <span className="plan-card__amount">{amount ? amount : "Enter amount"}</span>
              )}

              <span>
                / {pricingCycle ? lowercaseFirstLetterOfWords(pricingCycle) : "—"}
              </span>
            </p>
          )}
          {["Volume", "Tiered"].includes(pricingModel) && (
            <div className="plan-card__tiered">
              {pricingTiers?.length > 0 ? (
                pricingTiers?.map((pricingTier, index) => (
                  <div key={index}>
                    <h3 className="plan-card__value">
                      <span className="plan-card__currency">
                        {`${market?.currency ? market?.currency?.[0]?.sign : currencyFallback}`}
                      </span>
                      <span className="plan-card__amount">
                        {pricingTier?.unitPrice ? pricingTier.unitPrice : "Enter amount"}
                      </span>
                      <span className="plan-card__unit">{`/ (upto ${pricingTier?.to ? pricingTier?.to : "∞"} ${unitName ? unitName : "seat"})`}</span>
                      <span className="plan-card__unit">
                        / {pricingCycle ? lowercaseFirstLetterOfWords(pricingCycle) : "—"}
                      </span>
                    </h3>
                  </div>
                ))
              ) : (
                <div>No tiers added</div>
              )}
            </div>
          )}
          {["Per Unit"].includes(pricingModel) && (
            <div className="plan-card__tiered">
              <div>
                <h3 className="plan-card__value">
                  <span className="plan-card__currency">
                    {`${market?.currency ? market?.currency?.[0]?.sign : currencyFallback}`}
                  </span>
                  {title === "Product" && price && (
                    <span className="plan-card__amount">{price ? price : "Enter amount"}</span>
                  )}
                  {title === "Plan" && amount && (
                    <span className="plan-card__amount">{amount ? amount : "Enter amount"}</span>
                  )}
                  <span className="plan-card__unit">{`/ per ${unitName ? unitName : "seat"}`}</span>
                  <span className="plan-card__unit">
                    / {pricingCycle ? lowercaseFirstLetterOfWords(pricingCycle) : "—"}
                  </span>
                </h3>
              </div>
            </div>
          )}

          <h3 className="plan-card__billing">
            <span className="plan-card__billing-label">Billed</span>{" "}
            {billingCycle ? lowercaseFirstLetterOfWords(billingCycle) : "—"}
          </h3>
        </div>
      </div>

      <div className="plan-card__sections">
        <div className="plan-card__section">
          <h5 className="plan-card__section-title">Features</h5>
          <div className="plan-card__section-items">
            {features?.length > 0 ? (
              features?.map((feature, index) => (
                <div className="plan-card__section-item" key={index}>
                  <CheckCircle2 size={16} className="plan-card__section-item-icon" />
                  <p>{feature?.featureName}</p>
                </div>
              ))
            ) : isDrag || isPreview ? (
              <div className="plan-card__empty">
                <p>No features to show here</p>
              </div>
            ) : (
              <div className="plan-card__empty">
                <p>Select feature to show here</p>
              </div>
            )}
          </div>
        </div>
        {isAddOn && (
          <div className="plan-card__section">
            <h5 className="plan-card__section-title">Add-ons</h5>
            <div className="plan-card__section-items">
              {addOns?.length > 0 ? (
                addOns?.map((addon, index) => (
                  <div className="plan-card__section-item" key={index}>
                    <CheckCircle2 size={16} className="plan-card__section-item-icon" />
                    <p>{addon?.productName}</p>
                  </div>
                ))
              ) : isDrag || isPreview ? (
                <div className="plan-card__empty">
                  <p>No add ons available.</p>
                </div>
              ) : (
                <div className="plan-card__empty">
                  <p>Select add ons to show here</p>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="plan-card__section">
          <h5 className="plan-card__section-title">Usage compilations</h5>
          <div className="plan-card__section-items">
            {usageCompilationsArray?.length > 0 ? (
              usageCompilationsArray?.map((c, index) => (
                <div className="plan-card__section-item" key={index}>
                  <CheckCircle2 size={16} className="plan-card__section-item-icon" />
                  <div className="plan-card__compilation-info">
                    <p className="plan-card__compilation-title">{c?.title}</p>
                    {c?.source === "addon" && c?.productName && (
                      <p className="plan-card__compilation-source">from {c.productName}</p>
                    )}
                    {c?.source === "plan" && (
                      <p className="plan-card__compilation-source">plan compilation</p>
                    )}
                  </div>
                </div>
              ))
            ) : isDrag || isPreview ? (
              <div className="plan-card__empty">
                <p>No usage compilations available.</p>
              </div>
            ) : (
              <div className="plan-card__empty">
                <p>Select usage compilations to show here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanCard;
